import React from 'react';
import type { ReactNode } from 'react';
import { useQuery } from 'react-apollo';

import { LinkButton } from '@atlaskit/button/new';

import { buildCreationLink } from '@confluence/create-blank-fabric-page';
import { ErrorDisplay } from '@confluence/error-boundary';

import { GetPersonalSpaceKeyQuery } from '../GetPersonalSpaceKeyQuery.graphql';
import type { GetPersonalSpaceKeyQuery as GetPersonalSpaceKeyQueryType } from '../__types__/GetPersonalSpaceKeyQuery';

type HTV2CreateContentCTAProps = {
	appearance: 'primary' | 'default';
	children: ReactNode;
	contentType: 'page' | 'whiteboard';
	onClick: () => void;
	source: string;
};

type CtaComponentProps = { children: ReactNode; onClick: () => void };

const HTV2CreateContentCTA = ({
	appearance,
	children,
	contentType,
	onClick,
	source,
}: HTV2CreateContentCTAProps) => {
	const { data, error } = useQuery<GetPersonalSpaceKeyQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		GetPersonalSpaceKeyQuery,
	);
	const personalSpaceKey =
		(data?.user?.confluence?.hasPersonalSpace && data?.user?.confluence?.space?.key) || '';

	const getButtonHref = (): string => {
		return buildCreationLink({
			contentType,
			source,
			spaceKey: personalSpaceKey,
		});
	};

	return (
		<>
			{error && <ErrorDisplay error={error} />}
			<LinkButton
				onClick={onClick}
				isDisabled={Boolean(error || !personalSpaceKey)}
				href={getButtonHref()}
				appearance={appearance}
			>
				{children}
			</LinkButton>
		</>
	);
};

export const HTV2CreatePageCTA = ({ children, onClick }: CtaComponentProps) => {
	return (
		<HTV2CreateContentCTA
			source="homeOnboardingTakeoverV2CreatePage"
			contentType="page"
			onClick={onClick}
			appearance="primary"
		>
			{children}
		</HTV2CreateContentCTA>
	);
};

export const HTV2CreateWhiteboardCTA = ({ children, onClick }: CtaComponentProps) => {
	return (
		<HTV2CreateContentCTA
			source="homeOnboardingTakeoverV2CreateWhiteboard"
			contentType="whiteboard"
			onClick={onClick}
			appearance="default"
		>
			{children}
		</HTV2CreateContentCTA>
	);
};
